import styled, { css } from "styled-components";
import {
  elevation00,
  semanticRed01,
  shade0,
  shade300,
  shade600,
  shade700,
  shade800,
} from "../../styles/tokens";
import { normalStyles, xSmallStyles } from "../body/Body.Parts";

// STYLES
const containerFocusStyles = css`
  padding-top: 0.62rem;
  padding-bottom: 1rem;
  border: 1px solid ${shade0};
  label {
    ${xSmallStyles};
    padding-bottom: 0;
    color: ${shade300};
  }
`;

export const Container = styled.span<ContainerProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 1rem 0 0 1rem;
  justify-content: flex-start;
  width: ${({ $width = "23.125rem" }) => $width};
  min-height: ${({ $initialHeight = "8.875rem" }) => {
    const minAllowedHeight = 4.5;
    const initialHeightValue = parseFloat($initialHeight);
    const finalHeight = Math.max(initialHeightValue, minAllowedHeight);
    return `${finalHeight}rem`;
  }};
  max-height: ${({ $maxHeight, $initialHeight = "8.875rem" }) =>
    $maxHeight || $initialHeight};
  overflow: hidden;

  ${normalStyles};

  transition:
    0.3s ease border-color,
    0.3s ease box-shadow,
    0.3s ease padding;
  label {
    transition:
      0.3s ease font-size,
      0.3s ease line-height,
      0.3s ease top,
      0.3s ease color;
  }

  ${({ $disabled, $hasError, $emulateFocus }) =>
    $disabled
      ? css`
          cursor: not-allowed;
          color: ${shade700};
          border: 1px solid ${shade800};
        `
      : css`
          border: 1px solid ${$hasError ? semanticRed01 : shade800};

          &:hover {
            box-shadow: ${elevation00};
            ${!$hasError && `border-color: ${shade700}`};
          }

          &:focus-within {
            ${containerFocusStyles}
          }

          ${$emulateFocus && containerFocusStyles}
        `}

  ${({ $hasContent }) =>
    $hasContent &&
    css`
      padding-top: 0.62rem;
      padding-bottom: 1rem;
      label {
        ${xSmallStyles};
        padding-bottom: 0;
        color: ${shade300};
      }
    `}
`;

const textAreaFocusStyles = css`
  height: 100%;
  opacity: 1;
`;

export const InnerTextArea = styled.textarea<InnerTextAreaProps>`
  width: 100%;
  height: 0;
  opacity: 0;
  overflow-y: auto;

  height: ${({ $hasContent }) => ($hasContent ? "100%" : "0")};
  opacity: ${({ $hasContent }) => ($hasContent ? "1" : "0")};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};

  transition: opacity 0.8s ease;

  &::placeholder {
    color: ${shade600};
  }

  &:focus {
    ${textAreaFocusStyles};
  }
  ${normalStyles};
`;

// TYPES
export type Props = {
  label?: string;
  id?: string;
  hasError?: boolean;
  disabled?: boolean;
  emulateFocus?: boolean;
  initialHeight?: string;
  maxHeight?: string;
  width?: string;
};

type ContainerProps = {
  $hasContent?: boolean;
  $hasError?: boolean;
  $disabled?: boolean;
  $emulateFocus?: boolean;
  $initialHeight?: string;
  $maxHeight?: string;
  $width?: string;
};

type InnerTextAreaProps = Pick<
  ContainerProps,
  "$hasContent" | "$disabled" | "$emulateFocus"
>;
